<section class="app-page">
  <div class="px-12 py-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold">Auditors Dashboard</h1>
      <button (click)="refreshData()" class="btn btn-ghost btn-sm" [disabled]="nextAuditQuery.isFetching() || currentAuditQuery.isFetching()">
        <span class="loading loading-spinner loading-xs" *ngIf="nextAuditQuery.isFetching() || currentAuditQuery.isFetching()"></span>
        <span *ngIf="!nextAuditQuery.isFetching() && !currentAuditQuery.isFetching()">Refresh</span>
      </button>
    </div>

    @if (nextAuditQuery.isInitialLoading() || currentAuditQuery.isInitialLoading() || auditStatsQuery.isInitialLoading()) {
      <div class="flex justify-center items-center h-64">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
    } @else if (nextAuditQuery.isError() || currentAuditQuery.isError() || auditStatsQuery.isError()) {
      <div class="alert alert-error">
        <span>Error loading dashboard data. Please try again.</span>
        <button class="btn btn-sm btn-ghost" (click)="refreshData()">Retry</button>
      </div>
    } @else {
      <!-- Stats Cards Row -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <!-- In Progress Card -->
        <div class="card bg-base-100 shadow-sm border border-base-300">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">In Progress</p>
                <p class="text-3xl font-bold text-blue-600">{{ hasAuditInProgress() ? 1 : 0 }}</p>
              </div>
              <span nz-icon nzType="loading-3-quarters" class="text-4xl text-blue-500"></span>
            </div>
          </div>
        </div>

        <!-- Completed Today Card -->
        <div class="card bg-base-100 shadow-sm border border-base-300">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">Completed Today</p>
                <p class="text-3xl font-bold text-green-600">{{ auditStats()?.completedToday || 0 }}</p>
              </div>
              <span nz-icon nzType="check-circle" class="text-4xl text-green-500"></span>
            </div>
          </div>
        </div>

        <!-- This Month Card -->
        <div class="card bg-base-100 shadow-sm border border-base-300">
          <div class="card-body">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600 mb-1">This Month</p>
                <p class="text-3xl font-bold text-purple-600">{{ auditStats()?.completedThisMonth || 0 }}</p>
              </div>
              <span nz-icon nzType="bar-chart" class="text-4xl text-purple-500"></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Trips that arrived but are still missing footage (#805 Phase 4). -->
      <div class="mb-6">
        <buspro-admin-awaiting-media-widget />
      </div>

      <!-- Main Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Left Column: My Current Audit -->
        <div class="card bg-base-100 shadow-sm border border-base-300">
          <div class="card-body">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <span nz-icon nzType="loading-3-quarters" class="text-blue-500"></span>
              My Current Audit
            </h3>

            @if (currentAudit(); as audit) {
              <div class="space-y-4">
                <!-- Audit Info Card -->
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div class="flex items-center gap-3 mb-3">
                    <div class="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                      <span class="text-white font-bold text-sm">{{ audit.auditNumber }}</span>
                    </div>
                    <div class="flex-1">
                      <h4 class="font-bold text-gray-900">{{ audit.routeName }}</h4>
                      <p class="text-sm text-gray-600">Started {{ audit.auditReadyAt | amTimeAgo }}</p>
                    </div>
                    <span class="badge badge-warning badge-sm gap-1">
                      <span nz-icon nzType="loading-3-quarters" nzSpin></span>
                      In Progress
                    </span>
                  </div>
                </div>

                <!-- Resume Button -->
                <button
                  class="btn btn-primary btn-lg w-full gap-2 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  (click)="resumeAudit(audit.id)"
                >
                  <span nz-icon nzType="play-circle" nzTheme="fill"></span>
                  Resume Audit
                </button>

                <!-- View All Link -->
                <button class="btn btn-ghost btn-sm w-full" (click)="viewMyAudits()">
                  View All My Audits
                </button>
              </div>
            } @else {
              <div class="text-center py-8">
                <span nz-icon nzType="info-circle" class="text-4xl text-gray-400 mb-3"></span>
                <p class="text-gray-600 mb-4">No audit in progress</p>
                <button class="btn btn-outline btn-primary btn-sm" (click)="viewAllReadyAudits()">
                  View Available Audits
                </button>
              </div>
            }
          </div>
        </div>

        <!-- Right Column: Next Audit -->
        <div class="card bg-base-100 shadow-sm border border-base-300">
          <div class="card-body">
            <h3 class="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <span nz-icon nzType="thunderbolt" class="text-green-500"></span>
              Next Audit
            </h3>

            @if (nextAudit(); as next) {
              <div class="space-y-4">
                <!-- Next Audit Info Card -->
                <div class="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                  <div class="flex items-center gap-3 mb-3">
                    <div class="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                      <span class="text-white font-bold text-sm">{{ next.auditNumber }}</span>
                    </div>
                    <div class="flex-1">
                      <h4 class="font-bold text-gray-900">{{ next.routeName }}</h4>
                      <p class="text-sm text-gray-600">Ready {{ next.auditReadyAt | amTimeAgo }}</p>
                      <p class="text-xs text-gray-500 mt-1">{{ next.auditReadyAt | date: 'short' }}</p>
                    </div>
                    <span class="badge badge-success badge-sm gap-1">
                      <span nz-icon nzType="fire" nzTheme="fill"></span>
                      Priority
                    </span>
                  </div>
                </div>

                <!-- Start Audit Button -->
                <button
                  class="btn btn-success btn-lg w-full gap-2 shadow-lg transition-all duration-300"
                  [ngClass]="{
                    'hover:scale-105 hover:shadow-xl': canStartNextAudit(),
                    'btn-disabled opacity-50 cursor-not-allowed': !canStartNextAudit()
                  }"
                  [disabled]="!canStartNextAudit()"
                  [nz-tooltip]="getStartAuditTooltip()"
                  [class.loading]="startAuditMutation.isPending()"
                  (click)="startNextAudit()"
                >
                  <span nz-icon nzType="play-circle" nzTheme="fill"></span>
                  <span class="font-semibold">Start Audit</span>
                </button>

                <!-- View All Link -->
                <button class="btn btn-ghost btn-sm w-full" (click)="viewAllReadyAudits()">
                  View All Ready Audits
                </button>
              </div>
            } @else {
              <div class="text-center py-8">
                <span nz-icon nzType="check-circle" class="text-4xl text-green-400 mb-3"></span>
                <p class="text-gray-600">No operations ready for audit</p>
                <p class="text-sm text-gray-500 mt-2">All operations have been audited</p>
              </div>
            }
          </div>
        </div>
      </div>
    }
  </div>
</section>
